import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import API from "../lib/api";
import { CheckSquare, ArrowRight, Loader2, Users, AlertTriangle } from "lucide-react";

export default function Phase9Classification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditRun, advancePhase, refreshRun } = useAudit();
  const [findings, setFindings] = useState(() => location.state?.findings || []);
  const [teamData, setTeamData] = useState(() => location.state?.teamData || null);
  const [saving, setSaving] = useState(false);

  const fetchFindings = useCallback(async () => {
    if (!auditRun?.id) return;
    const { data } = await API.get(`/audit-runs/${auditRun.id}/findings`);
    const nextFindings = Array.isArray(data) ? data.filter((f) => !f.suppressed) : [];
    setFindings((prev) => (nextFindings.length > 0 ? nextFindings : prev));
  }, [auditRun?.id]);
  
  // Fetch team data for consolidated findings
  const fetchTeamData = useCallback(async () => {
    if (!auditRun?.id) return;
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/team`);
      setTeamData((prev) => {
        const hasFetchedFindings = Array.isArray(data?.consolidated_findings) && data.consolidated_findings.length > 0;
        return hasFetchedFindings ? data : (prev || data);
      });
    } catch (e) {
      console.error("Failed to fetch team data:", e);
    }
  }, [auditRun?.id]);

  useEffect(() => { 
    fetchFindings(); 
    fetchTeamData();
  }, [fetchFindings, fetchTeamData]);
  
  const isTeamMode = teamData?.audit_mode === "team";
  const consolidatedFindings = teamData?.consolidated_findings || [];
  const hasAnyFindings = findings.length > 0 || consolidatedFindings.length > 0;

  const updateSeverity = (id, severity) => {
    setFindings(prev => prev.map(f => f.id === id ? { ...f, final_severity: severity } : f));
  };

  const handleFinalize = async () => {
    setSaving(true);
    try {
      await API.put(`/audit-runs/${auditRun.id}/classification`, {
        findings: findings.map(f => ({
          id: f.id,
          final_severity: f.final_severity || f.suggested_severity
        }))
      });
      await advancePhase(10);
      await refreshRun();
      navigate("/phase/10");
      toast.success("Classification finalized. Proceeding to Report.");
    } catch (e) { toast.error("Failed to finalize"); }
    setSaving(false);
  };

  const sevCounts = { High: 0, Medium: 0, Low: 0, Observation: 0 };
  
  // Count solo findings
  findings.forEach(f => {
    const s = f.final_severity || f.suggested_severity;
    if (sevCounts[s] !== undefined) sevCounts[s]++;
  });
  
  // Count team consolidated findings
  consolidatedFindings.forEach(cf => {
    const s = cf.severity;
    if (sevCounts[s] !== undefined) sevCounts[s]++;
  });

  const heatmapColor = (s) => ({
    "High": "bg-red-500 dark:bg-red-600", "Medium": "bg-amber-500 dark:bg-amber-600",
    "Low": "bg-blue-500 dark:bg-blue-600", "Observation": "bg-slate-400 dark:bg-slate-600"
  }[s] || "bg-muted");

  return (
    <div className="space-y-6 max-w-3xl" data-testid="phase-9">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Phase 9: Classification Review</h2>
          <p className="text-sm text-muted-foreground mt-1">Finalize severity classifications for all findings.</p>
        </div>
        {isTeamMode && (
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
            <Users className="w-3 h-3 mr-1" /> Team Mode
          </Badge>
        )}
      </div>

      {/* Heatmap */}
      <Card>
        <CardHeader><CardTitle className="text-base">Severity Heatmap</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-3" data-testid="severity-heatmap">
            {Object.entries(sevCounts).map(([sev, count]) => (
              <div key={sev} className={`p-4 rounded-md text-white text-center ${heatmapColor(sev)}`}>
                <p className="text-3xl font-bold font-mono">{count}</p>
                <p className="text-xs font-semibold uppercase tracking-wider mt-1">{sev}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Team Consolidated Findings - Read Only */}
      {isTeamMode && consolidatedFindings.length > 0 && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-400" />
              Team Consolidated Findings ({consolidatedFindings.length})
              <Badge variant="outline" className="ml-2 bg-emerald-500/10 text-emerald-400 text-xs">Pre-validated</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {consolidatedFindings.map((cf, idx) => (
              <div key={cf.id || idx} className="flex items-center gap-3 p-3 border rounded-md bg-card">
                <Badge variant="outline" className={`text-xs shrink-0 ${
                  cf.severity === 'High' ? 'bg-red-500/10 text-red-500 border-red-500/30' :
                  cf.severity === 'Medium' ? 'bg-amber-500/10 text-amber-500 border-amber-500/30' :
                  cf.severity === 'Low' ? 'bg-blue-500/10 text-blue-500 border-blue-500/30' :
                  'bg-slate-500/10 text-slate-400 border-slate-500/30'
                }`}>
                  {cf.severity}
                </Badge>
                <p className="text-xs flex-1 line-clamp-1">{(() => {
                    const t = cf.finding_title || "";
                    return t
                      .replace(/^SQL audit FAIL:\s*/i, "Violation Detected: ")
                      .replace(/^SQL audit PASS:\s*/i, "Control Passed: ")
                      .replace(/^SQL audit\s+\w+:\s*/i, "") || t;
                  })()}</p>
                <span className="text-[10px] text-muted-foreground">Team validated</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Solo Findings Classification */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {isTeamMode ? "Additional Solo Findings" : "Classification"} ({findings.length} findings)
          </CardTitle>
        </CardHeader>
        <CardContent className={`space-y-3 ${findings.length === 0 ? "min-h-[260px] flex flex-col justify-center" : ""}`}>
          {findings.length === 0 && !isTeamMode && (
            <div className="text-center py-6 text-muted-foreground">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No findings to classify.</p>
              <p className="text-xs mt-1">Generate findings from evidence evaluation first.</p>
            </div>
          )}
          {findings.length === 0 && isTeamMode && (
            <p className="text-xs text-muted-foreground text-center py-4">
              No additional solo findings. Team findings above are ready for report.
            </p>
          )}
          {findings.map(f => (
            <div key={f.id} className="flex items-center gap-3 p-3 border rounded-md" data-testid={`classify-${f.id}`}>
              <span className="font-mono text-xs font-semibold w-20 shrink-0">{f.control_id}</span>
              <p className="text-xs flex-1 line-clamp-1">{f.finding_description}</p>
              <Select
                value={f.final_severity || f.suggested_severity}
                onValueChange={v => updateSeverity(f.id, v)}
              >
                <SelectTrigger className="w-[130px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Observation">Observation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleFinalize} disabled={saving || !hasAnyFindings} data-testid="finalize-classification-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckSquare className="w-4 h-4 mr-2" />}
          Finalize & Generate Report
        </Button>
      </div>
    </div>
  );
}
